import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  isDevMode,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXsrfConfiguration,
} from '@angular/common/http';
import { JwtModule } from '@auth0/angular-jwt';
import { authInitializer } from './initializers/auth.initializer';
import { authErrorInterceptor } from './interceptors/auth-error.interceptor';
import { subscriptionErrorInterceptor } from './interceptors/subscription-error.interceptor';
import { tokenRefreshInterceptor } from './interceptors/token-refresh.interceptor';
import { localeInterceptor } from './interceptors/locale.interceptor';
import { AuthService } from './services/auth/auth.service';
import { GlobalErrorHandlerService } from './services/logger/global-error-handler.service';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { provideServiceWorker } from '@angular/service-worker';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(authInitializer),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: () => inject(AuthService).getAccessToken(),
          // apiUrl may be relative (dev proxy, e.g. "/api/") or absolute (prod).
          // Resolve against the current origin so a relative value yields the
          // page host instead of throwing on `new URL`.
          allowedDomains: [new URL(environment.apiUrl, window.location.origin).host],
          disallowedRoutes: [/auth\/tokens/],
        },
      }),
    ),
    provideHttpClient(
      withInterceptors([
        localeInterceptor,
        tokenRefreshInterceptor,
        authErrorInterceptor,
        subscriptionErrorInterceptor,
      ]),
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'X-XSRF-TOKEN',
      }),
    ),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          cssLayer: true,
          darkModeSelector: false,
        },
      },
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
